import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Send, Users, Brain, Crown, Home, Plus,
  MessageCircle, TrendingUp, Heart, Eye
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MultiplayerMessaging = ({ user, onNavigateHome }) => {
  const [currentRoom, setCurrentRoom] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [ws, setWs] = useState(null);
  const [showTrustAnalytics, setShowTrustAnalytics] = useState(false);
  const [trustAnalytics, setTrustAnalytics] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.12) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    loadAvailableRooms();
  }, []);

  const loadAvailableRooms = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.get(`${API}/multiplayer/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableRooms(response.data.available_rooms);
    } catch (error) {
      console.error('Rooms load error:', error);
    }
  };

  const joinRoom = async (roomId) => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.post(`${API}/multiplayer/join-room/${roomId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentRoom(response.data.room);
      
      // Setup WebSocket connection
      const wsUrl = `ws://localhost:8001/ws/room/${roomId}`;
      const websocket = new WebSocket(wsUrl);
      
      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
        } else if (data.type === 'trust_tap') {
          // Update message trust score
          setMessages(prev => prev.map(msg => 
            msg.id === data.message_id 
              ? {...msg, trust_score: data.new_trust_score}
              : msg
          ));
        }
      };
      
      setWs(websocket);
      
    } catch (error) {
      console.error('Room join error:', error);
      alert('Failed to join consciousness salon. Check your trust resonance level.');
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || !currentRoom) return;

    try {
      const response = await axios.post(`${API}/multiplayer/send-message`, {
        room_id: currentRoom.id,
        user_id: user.id,
        message: currentMessage.trim()
      });

      setCurrentMessage('');
      
    } catch (error) {
      console.error('Message send error:', error);
      alert('Failed to send message to consciousness field');
    }
  };

  const trustTapMessage = async (messageId) => {
    try {
      await axios.post(`${API}/multiplayer/trust-tap`, {
        tapper_id: user.id,
        message_id: messageId,
        room_id: currentRoom.id
      });
      
      console.log('🤝 Trust resonance recorded anonymously');
      
    } catch (error) {
      console.error('Trust tap error:', error);
    }
  };

  const createNewRoom = async () => {
    try {
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.post(`${API}/multiplayer/create-room`, {
        name: `${user.name}'s Consciousness Salon`,
        room_type: "consciousness_salon",
        trust_threshold: user.trust_score > 150 ? 120 : 100
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await joinRoom(response.data.room.id);
      
    } catch (error) {
      console.error('Room creation error:', error);
      alert('Failed to create consciousness salon');
    }
  };

  if (!currentRoom) {
    // Room selection interface
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        {/* Grant spiral behind background */}
        <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `scale(8) rotate(${spiralPhase}deg)`,
              filter: 'blur(6px) saturate(1.8) brightness(1.4)',
              mixBlendMode: 'overlay'
            }}
          >
            <GrantitudeSpiral size={400} />
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

        <div className="relative z-10 max-w-6xl mx-auto p-6">
          
          {/* Header */}
          <div className="text-center pt-8 mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <Button
                onClick={onNavigateHome}
                variant="outline"
                className="text-white border-white/40 hover:bg-white/10"
              >
                <Home className="w-4 h-4 mr-2" />
                Return
              </Button>
            </div>
            
            <div className="flex items-center justify-center space-x-4 mb-6">
              <Users className="w-10 h-10 text-cyan-400" />
              <h1 
                className="text-4xl font-bold text-white"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 25px rgba(255, 255, 255, 0.4)'
                }}
              >
                Multiplayer Consciousness
              </h1>
            </div>
            <p 
              className="text-purple-200 text-xl"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Connect with consciousness travelers across timelines
            </p>
          </div>

          {/* Create New Room */}
          <div className="text-center mb-8">
            <Button
              onClick={createNewRoom}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-8 py-4 text-lg"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              <Plus className="w-6 h-6 mr-3" />
              Create Consciousness Salon
            </Button>
          </div>

          {/* Available Rooms */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableRooms.map((room, index) => (
              <button
                key={room.id}
                onClick={() => joinRoom(room.id)}
                className="group text-left"
              >
                <Card className="bg-black/40 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 
                          className="text-white text-lg font-bold"
                          style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                        >
                          {room.name}
                        </h3>
                        <p className="text-white/70 text-sm capitalize">{room.room_type.replace('_', ' ')}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-sm">Participants:</span>
                        <Badge variant="outline" className="text-cyan-300 border-cyan-400">
                          {room.participants.length}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-sm">Trust Required:</span>
                        <Badge 
                          variant={user.trust_score >= room.trust_threshold ? "default" : "outline"}
                          className={user.trust_score >= room.trust_threshold ? "bg-green-600" : "text-red-300 border-red-400"}
                        >
                          {room.trust_threshold}
                        </Badge>
                      </div>
                      
                      {room.participant_info && (
                        <div className="flex -space-x-2 mt-3">
                          {room.participant_info.slice(0, 4).map((participant, i) => (
                            <img
                              key={i}
                              src={participant.avatar_url}
                              alt=""
                              className="w-8 h-8 rounded-full border-2 border-white/30"
                            />
                          ))}
                          {room.participant_info.length > 4 && (
                            <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center text-xs text-white">
                              +{room.participant_info.length - 4}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
          
          {availableRooms.length === 0 && (
            <div className="text-center text-white/60 py-12">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-40" />
              <p 
                className="text-xl italic"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                No consciousness salons available at your trust level
              </p>
              <p className="text-sm mt-2">Build trust through authentic interactions to access more salons</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active room messaging interface
  return (
    <div 
      className="min-h-screen relative flex flex-col"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/80"></div>

      <div className="relative z-10 flex flex-col min-h-screen max-w-6xl mx-auto p-6">
        
        {/* Room Header */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => setCurrentRoom(null)}
                variant="outline"
                size="sm"
                className="text-white border-white/40"
              >
                ← Back to Salons
              </Button>
              
              <div>
                <h2 
                  className="text-white text-2xl font-bold"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  {currentRoom.name}
                </h2>
                <p className="text-white/70">{currentRoom.participants.length} consciousness travelers</p>
              </div>
            </div>

            <Button
              onClick={() => setShowTrustAnalytics(!showTrustAnalytics)}
              variant="outline"
              size="sm"
              className="text-purple-300 border-purple-400"
            >
              <Eye className="w-4 h-4 mr-1" />
              Trust Analytics
            </Button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 mb-6 bg-black/30 backdrop-blur-xl rounded-2xl border border-white/20 p-6 overflow-y-auto">
          <div className="space-y-4">
            {messages.map((msg, index) => (
              <div 
                key={msg.id} 
                className={`flex ${msg.user_id === user.id ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-md p-4 rounded-2xl shadow-lg cursor-pointer hover:shadow-xl transition-all ${
                    msg.user_id === user.id 
                      ? 'bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white' 
                      : 'bg-black/60 backdrop-blur-xl border border-white/20 text-white'
                  }`}
                  onDoubleClick={() => trustTapMessage(msg.id)}
                  title="Double-tap for trust resonance"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <img 
                      src={msg.avatar_url}
                      alt=""
                      className="w-6 h-6 rounded-full border border-white/30"
                    />
                    <span 
                      className="font-medium text-sm"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      {msg.user_name}
                    </span>
                    <span className="text-xs opacity-70">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                    
                    {msg.trust_taps && msg.trust_taps.length > 0 && (
                      <Badge variant="outline" className="text-xs text-green-300 border-green-400">
                        {msg.trust_taps.length} ♥
                      </Badge>
                    )}
                  </div>
                  
                  <p 
                    className="leading-relaxed"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    {msg.message}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Message Input */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
          <div className="flex space-x-4">
            <input
              type="text"
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Share with consciousness travelers..."
              className="flex-1 p-3 bg-white/10 border border-white/30 rounded-xl text-white focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            />
            
            <Button
              onClick={sendMessage}
              disabled={!currentMessage.trim()}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="text-center mt-3">
            <p 
              className="text-white/60 text-sm"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Double-tap messages for anonymous trust resonance • All interactions build secret trust analytics
            </p>
          </div>
        </div>
      </div>

      {/* Trust Analytics Sidebar */}
      {showTrustAnalytics && (
        <div className="absolute right-0 top-0 bottom-0 w-80 bg-black/80 backdrop-blur-xl border-l border-white/20 z-30 overflow-y-auto">
          <div className="p-6">
            <h3 
              className="text-white text-xl font-bold mb-6"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Trust Analytics
            </h3>
            
            <div className="space-y-4">
              <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                <h4 className="text-white font-medium mb-3">Your Trust Profile</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-white/70">Trust Score:</span>
                    <span className="text-green-300">{user.trust_score}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Level:</span>
                    <span className="text-purple-300">{user.consciousness_level}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Multiplayer:</span>
                    <span className="text-cyan-300">{user.multiplayer_access ? 'Enabled' : 'Limited'}</span>
                  </div>
                </div>
              </div>
              
              <div className="text-white/60 text-sm">
                <p className="italic">Secret trust analytics are maintained for each avatar to facilitate authentic collaboration and consciousness expansion.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerMessaging;